<script setup>
/**
 * What the ledger thinks the rows it just imported are.
 *
 * One line per transaction, the guesses directly under it, already chosen —
 * the work is *reading* it, not ticking a hundred boxes. Tap any chip to
 * change your mind about that one.
 *
 * The import has already happened by the time this opens. Cancelling here
 * leaves the rows exactly as they landed, unlabelled; it never unpicks the
 * import itself, because that is not what was cancelled.
 */
import { computed, onMounted, ref } from 'vue'
import Sheet from '../ui/Sheet.vue'
import Icon from '../ui/Icon.vue'
import Kbd from '../ui/Kbd.vue'
import { ariaCombo } from '../../lib/shortcuts'
import { fmt } from '../../stores'
import { closeSheet, state, toast } from '../../stores/state'
import { t } from '../../stores/i18n'
import { suggestionsFor, suggestionsReady } from '../../stores/suggest'
import { transactionsByIds } from '../../stores/transactions'
import { applyBulk } from '../../stores/bulk'
import { shortDay } from '../../lib/dates'

const props = defineProps({ payload: { type: Object, default: () => ({}) } })

const busy = ref(false)

/** The rows this is about, each with what was guessed and what is ticked. */
/** What was guessed for each row, and what is ticked, in the order they were imported. */
function review(txs) {
  const order = new Map((props.payload?.ids ?? []).map((id, i) => [id, i]))
  return [...txs]
    .sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0))
    .map((tx) => {
      const guess = suggestionsFor(tx)
      return {
        tx,
        guess,
        category: guess.category?.name ?? '',
        tags: guess.tags.map((tag) => tag.id),
      }
    })
    .filter((row) => row.guess.category || row.guess.tags.length)
}

// The import hands over the rows it just read, with the suggester already
// trained; opened any other way, both are asked for first.
const rows = ref(props.payload?.rows ? review(props.payload.rows) : [])
const loading = ref(!props.payload?.rows)
onMounted(async () => {
  if (!loading.value) return
  const [txs] = await Promise.all([transactionsByIds(props.payload?.ids ?? []), suggestionsReady()])
  rows.value = review(txs)
  loading.value = false
})

const tagName = (id) => state.tags.find((tag) => tag.id === id)?.name ?? ''
const percent = (confidence) => `${Math.round(confidence * 100)}%`

const toggleCategory = (row, name) => (row.category = row.category === name ? '' : name)
const toggleTag = (row, id) =>
  (row.tags = row.tags.includes(id) ? row.tags.filter((t) => t !== id) : [...row.tags, id])

const chosen = computed(() =>
  rows.value.filter((row) => row.category || row.tags.length).length,
)

const allOn = computed(() =>
  rows.value.every(
    (row) =>
      (!row.guess.category || row.category === row.guess.category.name) &&
      row.guess.tags.every((tag) => row.tags.includes(tag.id)),
  ),
)

function toggleAll() {
  const on = !allOn.value
  for (const row of rows.value) {
    row.category = on ? (row.guess.category?.name ?? '') : ''
    row.tags = on ? row.guess.tags.map((tag) => tag.id) : []
  }
}

/**
 * Applied in as few passes as possible: rows that ended up with the same
 * category and the same tags are one bulk update between them.
 */
async function accept() {
  busy.value = true
  try {
    const groups = new Map()
    for (const row of rows.value) {
      if (!row.category && !row.tags.length) continue
      const key = `${row.category}|${[...row.tags].sort().join(',')}`
      if (!groups.has(key)) groups.set(key, { category: row.category, tags: row.tags, ids: [] })
      groups.get(key).ids.push(row.tx.id)
    }
    for (const group of groups.values()) {
      await applyBulk(group.ids, {
        ...(group.category ? { category: group.category } : {}),
        addTags: group.tags,
      })
    }
    toast(t('suggest.applied', { count: chosen.value }), 'good')
  } finally {
    busy.value = false
  }
  closeSheet()
}
</script>

<template>
  <Sheet
    large
    :title="t('suggest.reviewTitle')"
    :subtitle="t('suggest.reviewSubtitle', { count: rows.length })"
    @close="closeSheet"
  >
    <div v-if="rows.length" class="grid gap-1">
      <button class="btn-ghost mb-1 justify-self-start !px-1 !text-[12.5px]" @click="toggleAll">
        {{ allOn ? t('suggest.clearAll') : t('suggest.acceptAll') }}
      </button>

      <div
        v-for="row in rows"
        :key="row.tx.id"
        class="rounded-xl border border-line-soft bg-raised/40 px-3 py-2.5"
      >
        <!-- The transaction, said the way the ledger says it. -->
        <div class="flex items-baseline gap-2">
          <span class="shrink-0 text-[11.5px] text-faint">{{ shortDay(row.tx.date) }}</span>
          <span class="min-w-0 flex-1 truncate text-[13.5px] font-medium">
            {{ row.tx.description }}
          </span>
          <span
            class="tnum shrink-0 text-[13px] font-semibold"
            :class="row.tx.amount > 0 ? 'text-accent' : ''"
          >
            {{ fmt(row.tx.amount, { signed: true }) }}
          </span>
        </div>

        <!-- And directly under it, what it is being called. -->
        <div class="mt-1.5 flex flex-wrap items-center gap-1.5">
          <button
            v-if="row.guess.category"
            class="chip !py-0.5 !text-[12px]"
            :class="{ 'chip-on': row.category === row.guess.category.name }"
            :aria-pressed="row.category === row.guess.category.name"
            @click="toggleCategory(row, row.guess.category.name)"
          >
            <Icon name="folder" :size="11" />
            {{ row.guess.category.name }}
            <span class="text-faint">{{ percent(row.guess.category.confidence) }}</span>
          </button>
          <button
            v-for="tag in row.guess.tags"
            :key="tag.id"
            class="chip !py-0.5 !text-[12px]"
            :class="{ 'chip-on': row.tags.includes(tag.id) }"
            :aria-pressed="row.tags.includes(tag.id)"
            @click="toggleTag(row, tag.id)"
          >
            <Icon name="tag" :size="11" />
            {{ tagName(tag.id) }}
            <span class="text-faint">{{ percent(tag.confidence) }}</span>
          </button>
        </div>
      </div>
    </div>

    <p v-else class="rounded-xl border border-line-soft bg-raised/50 p-4 text-center text-[13px] text-muted">
      {{ loading ? t('ledger.loading') : t('suggest.nothingToSay') }}
    </p>

    <template #footer>
      <p class="flex-1 text-[12px] text-faint">{{ t('suggest.cancelKeeps') }}</p>
      <button class="btn-soft" :disabled="busy" @click="closeSheet">{{ t('common.cancel') }}</button>
      <button data-primary :aria-keyshortcuts="ariaCombo('mod+enter')" class="btn-primary" :disabled="busy || !chosen" @click="accept">
        <Icon name="check" :size="16" /> {{ t('suggest.apply', { count: chosen }) }}
        <Kbd combo="mod+enter" />
      </button>
    </template>
  </Sheet>
</template>
